export type GoalAlignmentDisplayStatus = 'assessed' | 'unknown' | 'not_available';

export interface GoalAlignmentDisplay {
  status: GoalAlignmentDisplayStatus;
  alignmentRate: number | null;
  alignedItems: number;
  unalignedItems: number;
  unknownItems: number;
}

function safeCount(value: unknown): number {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

/**
 * Converts raw focus-to-goal matching counts into the state people should see.
 * Without any active goals there is no valid benchmark, so matched and unmatched
 * focus items are both "unknown" rather than evidence of misalignment.
 */
export function resolveGoalAlignmentDisplay(
  alignedItemsValue: unknown,
  unalignedItemsValue: unknown,
  activeGoalCountValue: unknown
): GoalAlignmentDisplay {
  const alignedItems = safeCount(alignedItemsValue);
  const unalignedItems = safeCount(unalignedItemsValue);
  const activeGoalCount = safeCount(activeGoalCountValue);

  if (activeGoalCount === 0) {
    return {
      status: 'unknown',
      alignmentRate: null,
      alignedItems: 0,
      unalignedItems: 0,
      unknownItems: alignedItems + unalignedItems,
    };
  }

  const assessedItems = alignedItems + unalignedItems;
  if (assessedItems === 0) {
    return {
      status: 'not_available',
      alignmentRate: null,
      alignedItems,
      unalignedItems,
      unknownItems: 0,
    };
  }

  return {
    status: 'assessed',
    alignmentRate: Math.round((alignedItems / assessedItems) * 100),
    alignedItems,
    unalignedItems,
    unknownItems: 0,
  };
}
